import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import {
  ArrowLeft, Calendar, DollarSign, Edit, Loader2, FileText,
  AlertCircle, Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { DateCell } from "@/components/DateCell";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface BlanketPoLine {
  id: string;
  lineNumber: number;
  supplierSku: string | null;
  description: string | null;
  committedQuantity: string | null;
  consumedQuantity: string | null;
  unitCost: string | null;
  uom: string;
  status: string;
  product?: { id: string; name: string; sku: string | null };
}

interface BlanketPo {
  id: string;
  blanketNumber: string;
  status: string;
  vendorId: string | null;
  commitmentType: string;
  pricingModel: string;
  committedValue: string | null;
  consumedValue: string | null;
  paymentTerms: string | null;
  incoterms: string | null;
  validFrom: string;
  validTo: string;
  approvedAt: string | null;
  notes: string | null;
  internalNotes: string | null;
  createdAt: string;
  vendor?: { id: string; name: string };
  lines?: BlanketPoLine[];
}

const STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "PENDING_APPROVAL", label: "Pending Approval" },
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "EXPIRED", label: "Expired" },
  { value: "CLOSED", label: "Closed" },
  { value: "CANCELLED", label: "Cancelled" },
];

function formatStatusLabel(s: string) {
  return s.replace(/_/g, " ").split(" ").map(w => w[0] + w.slice(1).toLowerCase()).join(" ");
}

export default function BlanketPoDetailPage() {
  const { sessionId } = useAuth();
  const [, params] = useRoute("/blanket-pos/:id");
  const [, setLocation] = useLocation();
  const [editOpen, setEditOpen] = useState(false);
  const [editStatus, setEditStatus] = useState("DRAFT");
  const [editNotes, setEditNotes] = useState("");
  const [editInternalNotes, setEditInternalNotes] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const id = params?.id;

  const { data: bpo, isLoading, error } = useQuery<BlanketPo>({
    queryKey: ["/api/blanket-pos", id],
    queryFn: () => api.get<BlanketPo>(`/blanket-pos/${id}`, sessionId),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, any>) =>
      api.patch<BlanketPo>(`/blanket-pos/${id}`, payload, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/blanket-pos", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/blanket-pos"] });
      toast({ title: "Blanket PO updated" });
      setEditOpen(false);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const handleEditOpen = () => {
    if (bpo) {
      setEditStatus(bpo.status || "DRAFT");
      setEditNotes(bpo.notes || "");
      setEditInternalNotes(bpo.internalNotes || "");
    }
    setEditOpen(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      status: editStatus,
      notes: editNotes || null,
      internalNotes: editInternalNotes || null,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-[300px] w-full rounded-lg" />
      </div>
    );
  }

  if (error || !bpo) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">Blanket PO not found</p>
        <Button variant="outline" onClick={() => setLocation("/blanket-pos")}>
          Back to Blanket POs
        </Button>
      </div>
    );
  }

  const lines = bpo.lines || [];
  const committedValue = Number(bpo.committedValue || 0);
  const consumedValue = Number(bpo.consumedValue || 0);
  const consumedPercent = committedValue > 0 ? Math.round((consumedValue / committedValue) * 100) : 0;

  const statusDotColor = (() => {
    switch (bpo.status) {
      case "DRAFT": return "bg-gray-400";
      case "PENDING_APPROVAL": return "bg-yellow-500";
      case "ACTIVE": return "bg-green-500";
      case "SUSPENDED": return "bg-orange-500";
      case "EXPIRED": return "bg-red-400";
      case "CLOSED": return "bg-slate-500";
      case "CANCELLED": return "bg-red-500";
      default: return "bg-gray-400";
    }
  })();

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLocation("/blanket-pos")}
                className="text-muted-foreground hover:text-foreground transition-colors"
                data-testid="button-back-to-blanket-pos"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="text-blanket-number">
                {bpo.blanketNumber}
              </h1>
            </div>
            <div className="flex items-center gap-2 text-2xl font-bold tracking-tight pl-7 flex-wrap">
              {bpo.vendor && bpo.vendorId && (
                <button
                  onClick={() => setLocation(`/vendors/${bpo.vendorId}`)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                  data-testid="link-vendor"
                >
                  {bpo.vendor.name}
                </button>
              )}
              {bpo.vendor && <span className="text-muted-foreground font-normal">/</span>}
              <span className="text-muted-foreground font-normal">Status:</span>
              <span data-testid="text-blanket-status">{formatStatusLabel(bpo.status)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={handleEditOpen} data-testid="button-edit-blanket-po">
              <Edit className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-0 border rounded-lg bg-card overflow-x-auto" data-testid="mini-cards-strip">
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <span className="text-xs text-muted-foreground">Status</span>
            <div className="flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-full shrink-0", statusDotColor)} />
              <span className="text-sm font-medium">{bpo.status.replace(/_/g, " ")}</span>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Committed</span>
              <p className="text-sm font-medium" data-testid="text-committed-value">
                {committedValue ? `$${committedValue.toLocaleString()}` : "—"}
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Consumed</span>
              <p className="text-sm font-medium" data-testid="text-consumed-value">
                ${consumedValue.toLocaleString()} ({consumedPercent}%)
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Valid From</span>
              <p className="text-sm font-medium" data-testid="text-valid-from">
                <DateCell value={bpo.validFrom} fallback="—" />
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Valid To</span>
              <p className="text-sm font-medium" data-testid="text-valid-to">
                <DateCell value={bpo.validTo} fallback="—" />
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="border-l-[3px] border-blue-500 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-500" />
              Agreement Details
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Commitment Type</span>
              <span className="text-sm font-medium uppercase">{bpo.commitmentType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Pricing Model</span>
              <span className="text-sm font-medium">{bpo.pricingModel?.replace(/_/g, " ")}</span>
            </div>
            {bpo.paymentTerms && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Payment Terms</span>
                <span className="text-sm font-medium">{bpo.paymentTerms}</span>
              </div>
            )}
            {bpo.incoterms && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Incoterms</span>
                <span className="text-sm font-medium">{bpo.incoterms}</span>
              </div>
            )}
            {bpo.approvedAt && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Approved</span>
                <span className="text-sm font-medium"><DateCell value={bpo.approvedAt} /></span>
              </div>
            )}
          </div>
        </div>

        <div className="border-l-[3px] border-gray-400 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Notes
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-3">
            {bpo.notes ? (
              <div>
                <span className="text-xs text-muted-foreground">External Notes</span>
                <p className="text-sm whitespace-pre-wrap" data-testid="text-notes">{bpo.notes}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No notes added.</p>
            )}
            {bpo.internalNotes && (
              <div>
                <span className="text-xs text-muted-foreground">Internal Notes</span>
                <p className="text-sm whitespace-pre-wrap" data-testid="text-internal-notes">{bpo.internalNotes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-l-[3px] border-emerald-500 bg-card rounded-r-lg shadow-sm">
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Package className="h-4 w-4 text-emerald-500" />
            Line Items
          </h2>
        </div>
        <div className="px-4 pb-4">
          {lines.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">Committed Qty</TableHead>
                    <TableHead className="text-right">Consumed Qty</TableHead>
                    <TableHead className="text-right">Unit Cost</TableHead>
                    <TableHead>UOM</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line) => (
                    <TableRow key={line.id} data-testid={`row-blanket-line-${line.lineNumber}`}>
                      <TableCell className="font-medium">{line.lineNumber}</TableCell>
                      <TableCell className="font-medium">{line.product?.name || line.description || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{line.supplierSku || line.product?.sku || "—"}</TableCell>
                      <TableCell className="text-right font-medium">{Number(line.committedQuantity || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right">{Number(line.consumedQuantity || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right font-medium">${Number(line.unitCost || 0).toFixed(4)}</TableCell>
                      <TableCell>{line.uom}</TableCell>
                      <TableCell>
                        <Badge variant={line.status === "ACTIVE" ? "default" : "secondary"} className="text-xs">
                          {line.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-24 text-muted-foreground">
              <Package className="h-10 w-10 mb-2 opacity-50" />
              <p>No line items</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Blanket PO</DialogTitle>
            <DialogDescription>Update the blanket purchase order information below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Status *</label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger data-testid="select-edit-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea
                placeholder="External notes..."
                className="min-h-[80px]"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                data-testid="textarea-edit-notes"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Internal Notes</label>
              <Textarea
                placeholder="Internal notes..."
                className="min-h-[80px]"
                value={editInternalNotes}
                onChange={(e) => setEditInternalNotes(e.target.value)}
                data-testid="textarea-edit-internal-notes"
              />
            </div>
            <div className="flex flex-wrap justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending} data-testid="button-save-blanket-po">
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
